import { useCallback, useEffect, useRef } from 'react';
import { useLocalSearchParams, router } from 'expo-router';
import { useUIStore } from '../store/uiStore';
import { defensePlansApi } from '../api/defensePlans';
import { Post, Structure } from '../types';
import { MapHandle } from '@components/map';
import { structureFocusInsets } from '../components/map/clearViewCenter';
import { useDesktopShell } from './useDesktopShell';

interface UseMapFocusParams {
  selectStructure: (structure: Structure | null) => void;
  getStructureById: (id: string) => Promise<Structure | null>;
  // Posts have no map-side selection concept (unlike structures, which open
  // the detail sheet) — a post focus just pans/zooms to its location.
  getPostById: (id: string) => Promise<Post | null>;
  mapRef: React.RefObject<MapHandle | null>;
}

export function useMapFocus({
  selectStructure,
  getStructureById,
  getPostById,
  mapRef,
}: UseMapFocusParams) {
  const isDesktopShell = useDesktopShell();
  // Fetch + select + center on a structure, or fetch + center on a post.
  // Shared by the two focus channels below. Latest-wins via a single request
  // counter across both (NOT effect-cleanup cancellation: both channels
  // clear their trigger state inside the effect, which re-runs it and would
  // cancel the still-in-flight fetch).
  const focusReqRef = useRef(0);
  const focusStructure = useCallback(
    (structureId: string) => {
      const req = ++focusReqRef.current;
      (async () => {
        const structure = await getStructureById(structureId);
        if (req !== focusReqRef.current || !structure) return;
        // Dock arbitration: a structure taking the docked panel closes the
        // plan editor and hides the plan viewer (the drawn plan itself stays
        // on the map). Covers this hook's own focus channels the way
        // index.tsx's press handlers cover the direct map-tap and
        // search-select paths.
        useUIStore.getState().closePlanPanel();
        useUIStore.getState().setViewedPlanId(null);
        selectStructure(structure);
        const handle = mapRef.current;
        if (!handle) return;
        const { latitude, longitude } = structure.centroid;
        // Web keeps the house clear of the detail dock that is opening;
        // native has no dock and centers.
        if (handle.focusInClearView) {
          handle.focusInClearView(latitude, longitude, 17, {
            insets: structureFocusInsets(isDesktopShell),
          });
        } else {
          handle.focusOnLocation(latitude, longitude, 17);
        }
      })();
    },
    [selectStructure, getStructureById, mapRef, isDesktopShell]
  );
  const focusPost = useCallback(
    (postId: string) => {
      const req = ++focusReqRef.current;
      (async () => {
        const post = await getPostById(postId);
        if (req !== focusReqRef.current || !post || !post.location) return;
        const handle = mapRef.current;
        if (!handle) return;
        // Web centers AND opens the post's hover preview so the target pin
        // is identifiable among neighbors; native has no hover surface, so
        // the centered pin itself is the affordance.
        if (handle.focusOnPost) {
          handle.focusOnPost(post, 17);
        } else {
          handle.focusOnLocation(post.location.latitude, post.location.longitude, 17);
        }
      })();
    },
    [getPostById, mapRef]
  );
  // Defense plan focus: opened from "Plans in this area", a plan card, or a
  // /plan/<id> share link. Fits the map to the plan's bounds when the map
  // supports it (web) and the plan has any (a plan with no features has
  // none); falls back to centering on the bounds' midpoint at a fixed zoom
  // otherwise. An empty plan (no bounds) moves nothing — there is nothing to
  // frame yet.
  const focusPlan = useCallback(
    (planId: string) => {
      const req = ++focusReqRef.current;
      (async () => {
        const plan = await defensePlansApi.getPlan(planId);
        if (req !== focusReqRef.current || !plan) return;
        useUIStore.getState().setViewedPlanId(plan.id);
        const handle = mapRef.current;
        if (!handle || !plan.bounds) return;
        if (handle.focusOnBounds) {
          handle.focusOnBounds(plan.bounds);
        } else {
          const latitude = (plan.bounds.n + plan.bounds.s) / 2;
          const longitude = (plan.bounds.e + plan.bounds.w) / 2;
          handle.focusOnLocation(latitude, longitude, 14);
        }
      })();
    },
    [mapRef]
  );

  // Deep-link focus: share links / external URLs land here with
  // ?focusStructureId=<id>&focusTs=<nonce> to auto-center + open the sheet.
  // The nonce ensures repeated taps re-fire the effect even when the id is
  // the same as last time (params alone wouldn't trigger a re-render).
  const { focusStructureId, focusTs } = useLocalSearchParams<{
    focusStructureId?: string;
    focusTs?: string;
  }>();
  useEffect(() => {
    if (!focusStructureId) return;
    focusStructure(focusStructureId);
    // Clear the params so back-navigation doesn't re-trigger on re-entry.
    router.setParams({ focusStructureId: undefined, focusTs: undefined });
  }, [focusStructureId, focusTs, focusStructure]);

  // In-app focus channel: Show on Map (structure/post detail screens)
  // stashes the target in uiStore because dismissTo/POP_TO drops params for
  // an already-mounted tab. Consume + clear; works both when the map is
  // live behind the stack and when it cold-mounts (effect runs on mount).
  const pendingMapFocus = useUIStore((s) => s.pendingMapFocus);
  useEffect(() => {
    if (!pendingMapFocus) return;
    useUIStore.getState().clearMapFocus();
    if (pendingMapFocus.kind === 'post') {
      focusPost(pendingMapFocus.id);
    } else if (pendingMapFocus.kind === 'plan') {
      focusPlan(pendingMapFocus.id);
    } else {
      focusStructure(pendingMapFocus.id);
    }
  }, [pendingMapFocus, focusStructure, focusPost, focusPlan]);
}
